var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import APPROVE_ORDER_SPECIFICATION_PRODUCTION from 'api/mutations/APPROVE_ORDER_SPECIFICATION_PRODUCTION';
import REVERT_ORDER_SPECIFICATION_PRODUCTION_APPROVAL from 'api/mutations/REVERT_ORDER_SPECIFICATION_PRODUCTION_APPROVAL';
import SET_ORDER_SPECIFICATION_MANUFACTURER from 'api/mutations/SET_ORDER_SPECIFICATION_MANUFACTURER';
import SET_ORDER_SPECIFICATION_PRIORITY from 'api/mutations/SET_ORDER_SPECIFICATION_PRIORITY';
import SET_ORDER_SPECIFICATION_PRODUCTION_START_DATE from 'api/mutations/SET_ORDER_SPECIFICATION_PRODUCTION_DATE';
import SET_ORDER_SPECIFICATION_VARIANT_ALLOCATION from 'api/mutations/SET_ORDER_SPECIFICATION_VARIANT_ALLOCATION';
import GET_ORDER_PRODUCTION_MANAGER_DATA from 'api/queries/GET_ORDER_PRODUCTION_MANAGER_DATA';
import GET_SPECIFICATION_PRODUCTION_MANAGER_DETAILS from 'api/queries/GET_SPECIFICATION_PRODUCTION_MANAGER_DETAILS';
import client from '../api/environment';
const initialState = {
    specifications: [],
    specificationDetails: null,
    specificationManufacturers: {},
    variantItemsChanges: {},
    allocationsReadyBySpec: {},
    status: {
        fetchOrderProductionManagerData: 'idle',
        fetchSpecificationDetails: 'idle',
        setOrderSpecificationPriority: 'idle',
        setOrderSpecificationManufacturer: 'idle',
        setOrderSpecificationProductionDate: 'idle',
        approveOrderSpecificationProduction: 'idle',
        revertOrderSpecificationProductionApproval: 'idle',
        setOrderSpecificationVariantAllocation: 'idle',
    },
    errors: {
        fetchOrderProductionManagerData: null,
        fetchSpecificationDetails: null,
        setOrderSpecificationPriority: null,
        setOrderSpecificationManufacturer: null,
        setOrderSpecificationProductionDate: null,
        approveOrderSpecificationProduction: null,
        revertOrderSpecificationProductionApproval: null,
        setOrderSpecificationVariantAllocation: null,
    },
    refetchFlags: {
        fetchOrderProductionManagerData: false,
        fetchSpecificationDetails: false,
    },
};
export const fetchOrderProductionManagerData = createAsyncThunk('orderProductionManager/fetchOrderProductionManagerData', (orderId, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield client.query({
            query: GET_ORDER_PRODUCTION_MANAGER_DATA,
            variables: {
                orderId,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.orderProductionManagerData;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchSpecificationDetails = createAsyncThunk('orderProductionManager/fetchSpecificationDetails', ({ orderId, specificationId }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    try {
        const response = yield client.query({
            query: GET_SPECIFICATION_PRODUCTION_MANAGER_DETAILS,
            variables: { orderId, specificationId },
            fetchPolicy: 'network-only',
        });
        const specification = (_a = response.data) === null || _a === void 0 ? void 0 : _a.specificationProductionManagerDetails;
        if (!specification) {
            return rejectWithValue(`Specification with ID ${specificationId} not found`);
        }
        return specification;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const setOrderSpecificationManufacturer = createAsyncThunk('orderProductionManager/setOrderSpecificationManufacturer', ({ orderId, specificationId, manufacturerId, manufacturer, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    var _b;
    try {
        const response = yield client.mutate({
            mutation: SET_ORDER_SPECIFICATION_MANUFACTURER,
            variables: { orderId, specificationId, manufacturerId },
        });
        const updated = (_b = response.data) === null || _b === void 0 ? void 0 : _b.setOrderSpecificationManufacturer;
        if (!updated) {
            return updated;
        }
        if (!manufacturer) {
            return updated;
        }
        return Object.assign(Object.assign({}, updated), { manufacturer: Object.assign(Object.assign({}, (updated.manufacturer || {})), manufacturer) });
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const setOrderSpecificationPriority = createAsyncThunk('orderProductionManager/setOrderSpecificationPriority', ({ orderId, specificationId, priority, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    var _c;
    try {
        const response = yield client.mutate({
            mutation: SET_ORDER_SPECIFICATION_PRIORITY,
            variables: { orderId, specificationId, priority },
        });
        return (_c = response.data) === null || _c === void 0 ? void 0 : _c.setOrderSpecificationPriority;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const setOrderSpecificationVariantAllocation = createAsyncThunk('orderProductionManager/setOrderSpecificationVariantAllocation', ({ orderId, specificationId, variantId, quantityAllocated, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    var _d;
    try {
        const response = yield client.mutate({
            mutation: SET_ORDER_SPECIFICATION_VARIANT_ALLOCATION,
            variables: {
                orderId,
                specificationId,
                orderComponentVariantId: variantId,
                quantityAllocated,
            },
        });
        return (_d = response.data) === null || _d === void 0 ? void 0 : _d.setOrderSpecificationVariantAllocation;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const setOrderSpecificationProductionStartDate = createAsyncThunk('orderProductionManager/setOrderSpecificationProductionStartDate', ({ orderId, specificationId, productionStartDate, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    var _e, _f, _g, _h;
    try {
        const response = yield client.mutate({
            mutation: SET_ORDER_SPECIFICATION_PRODUCTION_START_DATE,
            variables: { orderId, specificationId, productionStartDate },
        });
        return (_e = response.data) === null || _e === void 0 ? void 0 : _e.setOrderSpecificationProductionStartDate;
    }
    catch (error) {
        const apolloError = error;
        const graphQLError = (_f = apolloError.graphQLErrors) === null || _f === void 0 ? void 0 : _f[0];
        return rejectWithValue({
            message: apolloError.message,
            exceptionCode: ((_g = graphQLError === null || graphQLError === void 0 ? void 0 : graphQLError.extensions) === null || _g === void 0 ? void 0 : _g.exceptionCode) || null,
            exceptionMessage: ((_h = graphQLError === null || graphQLError === void 0 ? void 0 : graphQLError.extensions) === null || _h === void 0 ? void 0 : _h.exceptionMessage) || null,
        });
    }
}));
export const approveOrderSpecificationProduction = createAsyncThunk('orderProductionManager/approveOrderSpecificationProduction', ({ orderId, specificationId }, { rejectWithValue, dispatch }) => __awaiter(void 0, void 0, void 0, function* () {
    var _j, _k, _l, _m;
    try {
        const response = yield client.mutate({
            mutation: APPROVE_ORDER_SPECIFICATION_PRODUCTION,
            variables: { orderId, specificationId },
        });
        yield dispatch(fetchOrderProductionManagerData(orderId)).unwrap();
        return (_j = response.data) === null || _j === void 0 ? void 0 : _j.approveOrderSpecificationProduction;
    }
    catch (error) {
        const apolloError = error;
        const graphQLError = (_k = apolloError.graphQLErrors) === null || _k === void 0 ? void 0 : _k[0];
        return rejectWithValue({
            message: apolloError.message,
            exceptionCode: ((_l = graphQLError === null || graphQLError === void 0 ? void 0 : graphQLError.extensions) === null || _l === void 0 ? void 0 : _l.exceptionCode) || null,
            exceptionMessage: ((_m = graphQLError === null || graphQLError === void 0 ? void 0 : graphQLError.extensions) === null || _m === void 0 ? void 0 : _m.exceptionMessage) || null,
        });
    }
}));
export const revertOrderSpecificationProductionApproval = createAsyncThunk('orderProductionManager/revertOrderSpecificationProductionApproval', ({ orderId, specificationId }, { rejectWithValue, dispatch }) => __awaiter(void 0, void 0, void 0, function* () {
    var _o, _p, _q, _r;
    try {
        const response = yield client.mutate({
            mutation: REVERT_ORDER_SPECIFICATION_PRODUCTION_APPROVAL,
            variables: { orderId, specificationId },
        });
        yield dispatch(fetchOrderProductionManagerData(orderId)).unwrap();
        return (_o = response.data) === null || _o === void 0 ? void 0 : _o.revertOrderSpecificationProductionApproval;
    }
    catch (error) {
        const apolloError = error;
        const graphQLError = (_p = apolloError.graphQLErrors) === null || _p === void 0 ? void 0 : _p[0];
        return rejectWithValue({
            message: apolloError.message,
            exceptionCode: ((_q = graphQLError === null || graphQLError === void 0 ? void 0 : graphQLError.extensions) === null || _q === void 0 ? void 0 : _q.exceptionCode) || null,
            exceptionMessage: ((_r = graphQLError === null || graphQLError === void 0 ? void 0 : graphQLError.extensions) === null || _r === void 0 ? void 0 : _r.exceptionMessage) || null,
        });
    }
}));
export const submitSpecificationAllocations = createAsyncThunk('orderProductionManager/submitSpecificationAllocations', ({ orderId, specificationId }, { getState, rejectWithValue, dispatch }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const { orderProductionManager } = getState();
        const changes = orderProductionManager.variantItemsChanges || {};
        const dirtyVariants = Object.values(changes).filter((v) => v === null || v === void 0 ? void 0 : v.isDirty);
        yield Promise.all(dirtyVariants.map((v) => dispatch(setOrderSpecificationVariantAllocation({
            orderId,
            specificationId,
            variantId: v.variantId,
            quantityAllocated: v.assignedItems,
        })).unwrap()));
        return { ok: true };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const submitVariantItemsChanges = createAsyncThunk('orderProductionManager/submitVariantItemsChanges', ({ variantId, assignedItems, availableItems, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        // TODO: Add API call to backend when available
        // eslint-disable-next-line no-console
        console.log(`Submitting variant items changes for ${variantId}:`, {
            assignedItems,
            availableItems,
        });
        return { variantId, assignedItems, availableItems };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const orderProductionManagerSlice = createSlice({
    name: 'orderProductionManager',
    initialState,
    reducers: {
        resetOrderProductionManagerState(state) {
            state.specifications = [];
            state.specificationManufacturers = {};
            state.variantItemsChanges = {};
            state.allocationsReadyBySpec = {};
            state.status.fetchOrderProductionManagerData = 'idle';
            state.status.fetchSpecificationDetails = 'idle';
            state.status.setOrderSpecificationPriority = 'idle';
            state.status.setOrderSpecificationManufacturer = 'idle';
            state.status.setOrderSpecificationProductionDate = 'idle';
            state.status.approveOrderSpecificationProduction = 'idle';
            state.status.revertOrderSpecificationProductionApproval = 'idle';
            state.status.setOrderSpecificationVariantAllocation = 'idle';
            state.refetchFlags.fetchOrderProductionManagerData = false;
            state.refetchFlags.fetchSpecificationDetails = false;
        },
        resetSpecificationProductionManagerState(state) {
            state.specificationDetails = null;
            state.variantItemsChanges = {};
            state.status.fetchSpecificationDetails = 'idle';
            state.status.approveOrderSpecificationProduction = 'idle';
            state.status.revertOrderSpecificationProductionApproval = 'idle';
            state.status.setOrderSpecificationVariantAllocation = 'idle';
            state.refetchFlags.fetchSpecificationDetails = false;
        },
        setSpecificationAllocationsReady(state, action) {
            const { specificationId, ready } = action.payload;
            if (!specificationId) {
                return;
            }
            state.allocationsReadyBySpec[specificationId] = ready;
        },
        updateVariantAssignedItems(state, action) {
            const { variantId, delta } = action.payload;
            const variantState = state.variantItemsChanges[variantId];
            if (!variantState) {
                return;
            }
            const newAssignedItems = variantState.assignedItems + delta;
            const newAvailableItems = variantState.availableItems - delta;
            // Validate: assignedItems >= 0 and availableItems >= 0
            if (newAssignedItems < 0 || newAvailableItems < 0) {
                return;
            }
            variantState.assignedItems = newAssignedItems;
            variantState.availableItems = newAvailableItems;
            variantState.isDirty = true;
        },
        resetVariantItemsChanges(state, action) {
            const variantId = action.payload;
            const variantState = state.variantItemsChanges[variantId];
            if (!variantState) {
                return;
            }
            variantState.assignedItems = variantState.originalAssignedItems;
            variantState.availableItems = variantState.originalAvailableItems;
            variantState.isDirty = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrderProductionManagerData.pending, (state) => {
            state.status.fetchOrderProductionManagerData = 'loading';
        })
            .addCase(fetchOrderProductionManagerData.fulfilled, (state, action) => {
            state.status.fetchOrderProductionManagerData = 'succeeded';
            state.specifications = action.payload;
            state.refetchFlags.fetch = false;
            state.refetchFlags.fetchOrderProductionManagerData = false;
        })
            .addCase(fetchOrderProductionManagerData.rejected, (state, action) => {
            state.status.fetchOrderProductionManagerData = 'failed';
            state.errors.fetchOrderProductionManagerData =
                action.payload || 'Failed to fetch production manager data';
            state.refetchFlags.fetch = false;
            state.refetchFlags.fetchOrderProductionManagerData = false;
        })
            .addCase(fetchSpecificationDetails.pending, (state) => {
            state.status.fetchSpecificationDetails = 'loading';
        })
            .addCase(fetchSpecificationDetails.fulfilled, (state, action) => {
            state.status.fetchSpecificationDetails = 'succeeded';
            state.specificationDetails = action.payload;
            state.refetchFlags.fetchSpecificationDetails = false;
            // Initialize variantItemsChanges for all variants in the specification
            const specification = action.payload;
            if (specification.components) {
                specification.components.forEach((comp) => {
                    var _a;
                    (_a = comp.componentVariants) === null || _a === void 0 ? void 0 : _a.forEach((variant) => {
                        var _a, _b;
                        if (!state.variantItemsChanges[variant.id]) {
                            const allocated = (_a = variant.allocatedQuantity) !== null && _a !== void 0 ? _a : 0;
                            const available = (_b = variant.availableQuantity) !== null && _b !== void 0 ? _b : 0;
                            state.variantItemsChanges[variant.id] = {
                                variantId: variant.id,
                                // Assigned == allocated to this specification
                                assignedItems: allocated,
                                // Available == global remaining (collected - allocated across all specs)
                                availableItems: available,
                                originalAssignedItems: allocated,
                                originalAvailableItems: available,
                                isDirty: false,
                            };
                        }
                    });
                });
            }
        })
            .addCase(fetchSpecificationDetails.rejected, (state, action) => {
            state.status.fetchSpecificationDetails = 'failed';
            state.errors.fetchSpecificationDetails =
                action.payload || 'Failed to fetch specification details';
            state.refetchFlags.fetchSpecificationDetails = false;
        })
            .addCase(setOrderSpecificationManufacturer.pending, (state) => {
            state.status.setOrderSpecificationManufacturer = 'loading';
        })
            .addCase(setOrderSpecificationManufacturer.fulfilled, (state, action) => {
            var _a, _b;
            state.status.setOrderSpecificationManufacturer = 'succeeded';
            const updated = action.payload;
            if (((_a = updated === null || updated === void 0 ? void 0 : updated.specification) === null || _a === void 0 ? void 0 : _a.id) && ((_b = state.specifications) === null || _b === void 0 ? void 0 : _b.specifications)) {
                state.specifications.specifications = state.specifications.specifications.map((s) => {
                    var _a;
                    return ((_a = s === null || s === void 0 ? void 0 : s.specification) === null || _a === void 0 ? void 0 : _a.id) === updated.specification.id
                        ? Object.assign(Object.assign(Object.assign({}, s), updated), { 
                            // don't clobber full spec fields if backend returns only id in some cases
                            specification: Object.assign(Object.assign({}, (s.specification || {})), (updated.specification || {})) }) : s;
                });
            }
        })
            .addCase(setOrderSpecificationManufacturer.rejected, (state, action) => {
            state.status.setOrderSpecificationManufacturer = 'failed';
            state.errors.setOrderSpecificationManufacturer =
                action.payload || 'Failed to set specification manufacturer';
        })
            .addCase(setOrderSpecificationPriority.pending, (state) => {
            state.status.setOrderSpecificationPriority = 'loading';
        })
            .addCase(setOrderSpecificationPriority.fulfilled, (state, action) => {
            var _a, _b;
            state.status.setOrderSpecificationPriority = 'succeeded';
            const updated = action.payload;
            if (((_a = updated === null || updated === void 0 ? void 0 : updated.specification) === null || _a === void 0 ? void 0 : _a.id) && ((_b = state.specifications) === null || _b === void 0 ? void 0 : _b.specifications)) {
                state.specifications.specifications = state.specifications.specifications.map((s) => {
                    var _a;
                    return ((_a = s === null || s === void 0 ? void 0 : s.specification) === null || _a === void 0 ? void 0 : _a.id) === updated.specification.id
                        ? Object.assign(Object.assign(Object.assign({}, s), updated), { specification: Object.assign(Object.assign({}, (s.specification || {})), (updated.specification || {})) }) : s;
                });
            }
        })
            .addCase(setOrderSpecificationPriority.rejected, (state, action) => {
            state.status.setOrderSpecificationPriority = 'failed';
            state.errors.setOrderSpecificationPriority =
                action.payload || 'Failed to set specification priority';
        })
            .addCase(setOrderSpecificationProductionStartDate.pending, (state) => {
            state.status.setOrderSpecificationProductionDate = 'loading';
        })
            .addCase(setOrderSpecificationProductionStartDate.fulfilled, (state, action) => {
            var _a, _b;
            state.status.setOrderSpecificationProductionDate = 'succeeded';
            const updated = action.payload;
            if (((_a = updated === null || updated === void 0 ? void 0 : updated.specification) === null || _a === void 0 ? void 0 : _a.id) && ((_b = state.specifications) === null || _b === void 0 ? void 0 : _b.specifications)) {
                state.specifications.specifications = state.specifications.specifications.map((s) => {
                    var _a;
                    return ((_a = s === null || s === void 0 ? void 0 : s.specification) === null || _a === void 0 ? void 0 : _a.id) === updated.specification.id
                        ? Object.assign(Object.assign(Object.assign({}, s), updated), { specification: Object.assign(Object.assign({}, (s.specification || {})), (updated.specification || {})) }) : s;
                });
            }
        })
            .addCase(setOrderSpecificationProductionStartDate.rejected, (state, action) => {
            state.status.setOrderSpecificationProductionDate = 'failed';
            const payload = action.payload;
            state.errors.setOrderSpecificationProductionDate =
                (payload === null || payload === void 0 ? void 0 : payload.message) || 'Failed to set specification production date';
        })
            .addCase(approveOrderSpecificationProduction.pending, (state) => {
            state.status.approveOrderSpecificationProduction = 'loading';
        })
            .addCase(approveOrderSpecificationProduction.fulfilled, (state) => {
            state.status.approveOrderSpecificationProduction = 'succeeded';
        })
            .addCase(approveOrderSpecificationProduction.rejected, (state, action) => {
            state.status.approveOrderSpecificationProduction = 'failed';
            const payload = action.payload;
            state.errors.approveOrderSpecificationProduction =
                (payload === null || payload === void 0 ? void 0 : payload.message) || 'Failed to approve specification production';
        })
            .addCase(revertOrderSpecificationProductionApproval.pending, (state) => {
            state.status.revertOrderSpecificationProductionApproval = 'loading';
        })
            .addCase(revertOrderSpecificationProductionApproval.fulfilled, (state) => {
            state.status.revertOrderSpecificationProductionApproval = 'succeeded';
        })
            .addCase(revertOrderSpecificationProductionApproval.rejected, (state, action) => {
            state.status.revertOrderSpecificationProductionApproval = 'failed';
            const payload = action.payload;
            state.errors.revertOrderSpecificationProductionApproval =
                (payload === null || payload === void 0 ? void 0 : payload.message) || 'Failed to revert specification production approval';
        })
            .addCase(setOrderSpecificationVariantAllocation.pending, (state) => {
            state.status.setOrderSpecificationVariantAllocation = 'loading';
        })
            .addCase(setOrderSpecificationVariantAllocation.fulfilled, (state, action) => {
            var _a, _b;
            state.status.setOrderSpecificationVariantAllocation = 'succeeded';
            const updated = action.payload;
            if ((updated === null || updated === void 0 ? void 0 : updated.orderComponentVariantId) &&
                state.variantItemsChanges[updated.orderComponentVariantId]) {
                const variantState = state.variantItemsChanges[updated.orderComponentVariantId];
                variantState.assignedItems = (_a = updated.allocatedQuantity) !== null && _a !== void 0 ? _a : variantState.assignedItems;
                variantState.availableItems = (_b = updated.availableQuantity) !== null && _b !== void 0 ? _b : variantState.availableItems;
                variantState.originalAssignedItems = variantState.assignedItems;
                variantState.originalAvailableItems = variantState.availableItems;
                variantState.isDirty = false;
            }
        })
            .addCase(setOrderSpecificationVariantAllocation.rejected, (state, action) => {
            state.status.setOrderSpecificationVariantAllocation = 'failed';
            state.errors.setOrderSpecificationVariantAllocation =
                action.payload || 'Failed to set variant allocation';
        })
            .addCase(submitVariantItemsChanges.fulfilled, (state, action) => {
            const { variantId, assignedItems, availableItems } = action.payload;
            const variantState = state.variantItemsChanges[variantId];
            if (variantState) {
                variantState.originalAssignedItems = assignedItems;
                variantState.originalAvailableItems = availableItems;
                variantState.isDirty = false;
            }
        })
            .addCase(submitVariantItemsChanges.rejected, (state, action) => {
            // eslint-disable-next-line no-console
            console.error('Failed to submit variant items changes:', action.payload);
        });
    },
});
export const { resetOrderProductionManagerState, resetSpecificationProductionManagerState, setSpecificationAllocationsReady, updateVariantAssignedItems, resetVariantItemsChanges, } = orderProductionManagerSlice.actions;
export const selectOrderProductionManagerStatus = (state) => state.orderProductionManager.status;
export const selectOrderProductionManagerErrors = (state) => state.orderProductionManager.errors;
export const selectOrderProductionManagerRefetchFlags = (state) => state.orderProductionManager.refetchFlags;
export const selectOrderProductionManagerData = (state) => state.orderProductionManager.specifications;
export const selectOrderProductionManagerSpecificationDetails = (state) => state.orderProductionManager.specificationDetails;
export const selectOrderProductionManagerManufacturers = (state) => state.orderProductionManager.specificationManufacturers;
export const selectSpecificationManufacturer = (specificationId) => (state) => state.orderProductionManager.specificationManufacturers[specificationId] || null;
export const selectVariantItemsChanges = (state) => state.orderProductionManager.variantItemsChanges;
export const selectVariantItemsState = (variantId) => (state) => state.orderProductionManager.variantItemsChanges[variantId] || null;
export const selectHasDirtyVariants = (state) => {
    const changes = state.orderProductionManager.variantItemsChanges;
    return Object.values(changes).some((variant) => variant.isDirty);
};
export default orderProductionManagerSlice.reducer;
